const watch = require('redux-watch');
const skuUtils = require('utils/Sku');
const cookieUtils = require('utils/Cookies');
const localeUtils = require('utils/LanguageLocale');
const helperUtils = require('utils/Helpers');
const orderUtils = require('utils/Order');
const UrlUtils = require('utils/Url');
const Storage = require('utils/localStorage/Storage');
const store = require('Store');
const LOCAL_STORAGE = require('utils/localStorage/Constants');
const SHIP_COUNTRY_COOKIE = 'ship_country';
const locationUtils = require('utils/Location');
const auth = require('utils/Authentication');

const PROFILE_STATUS = {
    LOGGED_IN: 4,
    RECOGNIZED: 2,
    ANONYMOUS: 0
};

const USER_DATA_EXPIRY = Storage.HOURS * 1;

const getRewardsLabelsObj = () => {
    const getText = localeUtils.getLocaleResourceFile('utils/locales/User', 'User');
    return {
        CELEBRATION_GIFT: {
            TITLE: getText('celebrationGiftTitle'),
            SUBTITLE: getText('celebrationGiftSubtitle')
        },
        REWARDS: {
            TITLE: getText('rewardsTitle')
        },
        BIRTHDAY_GIFT: {
            TITLE: getText('birthdayGiftTitle'),
            SUBTITLE: getText('birthdayGiftSubtitle')
        }
    };
};

const getInfoModalWarningTitle = () => {
    const getText = localeUtils.getLocaleResourceFile('utils/locales/User', 'User');
    return getText('infoModalWarningTitle');
};

let userUtils = {
    SHIP_COUNTRY_COOKIE,

    PROFILE_STATUS,

    USER_DATA_EXPIRY,

    rewardsLabels: getRewardsLabelsObj(),

    INFO_MODAL_WARNING_TITLE: getInfoModalWarningTitle(),

    types: {
        NON_BI: 'NON_BI',
        BI: 'BI',
        VIB: 'VIB',
        ROUGE: 'ROUGE',
        BI_DOWN: 'BI_DOWN',
        LOGGED_IN: 'loggedin',
        RECOGNIZED: 'recognized',
        UNRECOGNIZED: 'unrecognized',
        FLASH_AUTO_RENEW: 'Flash-AR'
    },

    getBiStatus: function () {
        let user = store.getState().user;
        return user.beautyInsiderAccount ? user.beautyInsiderAccount.vibSegment : this.types.NON_BI;
    },

    getRealTimeBiStatus: function () {
        let user = store.getState().user;
        return user.beautyInsiderAccount ? user.beautyInsiderAccount.realTimeVIBStatus :
            this.types.NON_BI;
    },

    getBiAccountInfo: function () {
        let user = store.getState().user;
        return user.beautyInsiderAccount;
    },

    getBiStatusText: function (status) {
        let biStatus = status || this.getBiStatus();
        switch (biStatus) {
            case this.types.VIB:
                return 'VIB';
            case this.types.ROUGE:
                return 'Rouge';
            case this.types.BI:
                return 'Insider';
            default:
                return this.types.NON_BI;
        }
    },

    isCelebrationEligible: function (beautyInsiderAccount) {
        const biAccount = beautyInsiderAccount ||
            store.getState().user.beautyInsiderAccount || {};

        return biAccount.eligibleForCelebrationGift;
    },

    isBirthdayGiftEligible: function () {
        let biAccount = store.getState().user.beautyInsiderAccount;
        return (biAccount && biAccount.eligibleForBirthdayGift);
    },

    getProfileId: function () {
        let user = store.getState().user;
        return user.profileId;
    },

    getPublicId: function () {
        let user = store.getState().user;
        return user.publicId;
    },

    getProfileStatus: function () {
        let user = store.getState().user;
        return user.profileStatus;
    },

    isFlash: function () {
        let user = store.getState().user;
        return user.isFlash || false;
    },

    getProfileFirstName: function () {
        let user = store.getState().user;
        return user.firstName;
    },

    getProfileLastName: function () {
        let user = store.getState().user;
        return user.lastName;
    },

    getUserPhoneNumber: function () {
        let user = store.getState().user;
        return user.phoneNumber;
    },

    isPreApprovedForCreditCard: function () {
        let biInfo = this.getBiAccountInfo();
        return biInfo && biInfo.ccAccountandPrescreenInfo &&
            biInfo.ccAccountandPrescreenInfo.preScreenStatus;
    },

    getSephoraCreditCardInfo: function () {
        let biInfo = this.getBiAccountInfo();
        return biInfo && biInfo.ccAccountandPrescreenInfo;
    },

    getCreditCardType: function () {
        let user = store.getState().user;
        return user.ccCardType;
    },

    getBankRewards: function () {
        let user = store.getState().user;
        return user.bankRewards || {};
    },

    getRewardsAmount: function (bankRewards) {
        return bankRewards && bankRewards.rewardsTotal ? bankRewards.rewardsTotal : 0;
    },

    getGiftLastDateToRedeem: function () {
        const beautyInsiderAccount = store.getState().user.beautyInsiderAccount;
        return beautyInsiderAccount ? beautyInsiderAccount.bdGiftLastDateToRedeem : null;
    },

    isFlashAutoRenewed: function () {
        let user = store.getState().user;
        let userSubscriptions = user.subscriptionPrograms;
        if (userSubscriptions && userSubscriptions.flash) {
            if (userSubscriptions.flash.subscriptions &&
                userSubscriptions.flash.subscriptions.length) {
                let subscription = userSubscriptions.flash.subscriptions[0];
                return subscription === this.types.FLASH_AUTO_RENEW;
            }
        }
        return false;
    },

    isBI: function () {
        return this.getBiStatus() !== this.types.NON_BI;
    },

    isRouge: function () {
        return this.getBiStatus() === this.types.ROUGE;
    },

    isVIB: function () {
        return this.getBiStatus() === this.types.VIB;
    },

    isAnonymous: function (userData) {
        let user = userData || store.getState().user;
        return typeof user.profileStatus === 'undefined' ||
            user.profileStatus === PROFILE_STATUS.ANONYMOUS;
    },

    isSignedIn: function () {
        return store.getState().user.profileStatus === PROFILE_STATUS.LOGGED_IN;
    },

    isRecognized: function () {
        return store.getState().user.profileStatus === PROFILE_STATUS.RECOGNIZED;
    },

    isUserAtleastRecognized: function () {
        const profileStatus = store.getState().user.profileStatus;
        return (profileStatus === PROFILE_STATUS.LOGGED_IN) ||
            (profileStatus === PROFILE_STATUS.RECOGNIZED);
    },

    isBiLevelQualifiedFor: function (sku) {
        const biTierMatrix = {
            none: 0,
            bi: 1,
            vib: 2,
            rouge: 3,
            /*eslint-disable camelcase*/
            non_bi: 0
        };

        //if sku is a bi reward and exclusive level is none, then reward is lowest level (bi)
        //biExclusiveLevel is set to vib or rouge when reward can only be purchased by those tiers
        let skuTier = skuUtils.isBiReward(sku) && sku.biExclusiveLevel === 'none' ?
            'bi' : (sku.biExclusiveLevel || 'none').toLowerCase();
        let userTier = (this.getBiStatus()).toLowerCase();
        let isTierMet = biTierMatrix[userTier] >= biTierMatrix[skuTier];

        return isTierMet;
    },

    isBiPointsBiQualifiedFor: function (sku) {
        let basket = store.getState().basket;
        let skuPoints = skuUtils.getBiPoints(sku);

        // bday gift, welcome kit, etc dont require points to be added to cart
        if (!skuPoints) {
            return true;
        } else {
            return skuPoints <= basket.availableBiPoints - basket.redeemedBiPoints;
        }
    },

    isRewardEligible: function (sku) {
        //checks sku BI level against users
        if (!this.isBiLevelQualifiedFor(sku)) {
            return false;
        }

        //checks the points of BI user against reward item
        return this.isBiPointsBiQualifiedFor(sku);

    },

    //Gets the current logged in user
    getUser: function () {
        return new Promise((resolve, reject) => {
            const userData = store.getState('user');
            const userWatch = watch(store.getState, 'user');

            if (userData.user && userData.user.profileId) {
                resolve(userData.user);
            } else {
                // Here, we're acting on a premise that the user can change only
                // once, i.e. on sign in. The page is supposed to get reloaded
                // always when user signs out.
                let unsubscribe = store.subscribe(userWatch(user => {
                    resolve(user);
                    unsubscribe();
                }), this);
            }
        });
    },

    /**
     * Validates that user state has been set
     * @returns {Promise}
     */
    validateUserStatus: function () {
        return new Promise((resolve, reject) => {
            const user = store.getState('user').user;
            const userWatch = watch(store.getState, 'user');

            if (user.profileLocale) {
                resolve(user);
            } else {
                // Here, we're acting on a premise that the user can change only
                // once, i.e. on sign in. The page is supposed to get reloaded
                // always when user signs out.
                let unsubscribe = store.subscribe(userWatch(newUser => {
                    resolve(newUser);
                    unsubscribe();
                }), this);
            }
        });
    },

    /** cleans up the various nested BI personalization information for display purposes.
      * params: personalizedInformation object from the beautyInsiderAccount object inside of user
      * returns either cleaned up information or an empty object
    **/
    biPersonalInfoDisplayCleanUp: function (personalizedInfo) {
        let personalInfo = Object.assign({}, personalizedInfo);
        let cherryPickInfo = function (objArray) {
            let newArray = [];
            objArray.forEach((obj) => {
                if (obj.hasOwnProperty('isSelected')) {
                    newArray.push(obj.displayName);
                }
            });
            if (newArray.length === 1) {
                return newArray[0];
            } else if (newArray.length === 0) {
                return null;
            } else {
                return newArray.join(', ');
            }
        };

        for (let key in personalInfo) {
            if (personalInfo.hasOwnProperty(key)) {
                let newDisplayValue = cherryPickInfo(personalInfo[key]);
                if (newDisplayValue) {
                    personalInfo[key] = newDisplayValue;
                } else {
                    personalInfo[key] = null;
                }
            }
        }

        return personalInfo;
    },

    /** if all the keys in the cleaned up bi personal info are empty except for gender
      * (because gender is always provided upon BI registration as true for female),
      * indicating that the user has not chosen any personalized information, return true
    **/
    isBiPersonalInfoEmpty: function (personalInfo) {
        let isEmpty = true;
        for (let key in personalInfo) {
            if (key !== 'gender' && personalInfo[key]) {
                isEmpty = false;
            }
        }

        return isEmpty;
    },

    //converts number for display with k for thousands and m for millions
    //param: number
    formatSocialCount: function (num) {
        switch (true) {
            case (num >= 1000000):
                return helperUtils.decimalFloor(num / 1000000, -1).toString()
                    .replace(/\.0$/, '') + 'm';
            case (num >= 1000):
                return helperUtils.decimalFloor(num / 1000, -1).toString()
                    .replace(/\.0$/, '') + 'k';
            default:
                return num;
        }
    },

    getShippingCountry: function () {
        let countryCode = cookieUtils.read(SHIP_COUNTRY_COOKIE) || localeUtils.getCurrentCountry() || '';
        return {
            countryCode: countryCode.toUpperCase(),
            countryLongName: localeUtils.getCountryLongName(countryCode),
            countryFlagImage: localeUtils.getCountryFlagImage(countryCode)
        };
    },

    setShippingCountry: function (country) {
        if (country && country.countryCode) {
            cookieUtils.write(SHIP_COUNTRY_COOKIE, country.countryCode);
        }
    },

    refreshShippingCountry: function () {
        this.setShippingCountry(this.getShippingCountry());
    },

    isSocial: function () {
        return !!(store.getState().user.nickName);
    },

    getNickname: function () {
        return store.getState().user.nickName;
    },

    needsSocialReOpt: function () {
        return !store.getState().user.isSocialEnabled;
    },

    displayBiStatus: function (vibSegment) {
        switch (vibSegment) {
            case this.types.VIB:
                return 'VIB';
            case this.types.ROUGE:
                return 'ROUGE';
            default:
                return 'INSIDER';
        }
    },

    getSocialInfo: function () {
        let socialInfoWatch = watch(store.getState, 'socialInfo');

        return new Promise((resolve, reject) => {
            let lithiumSuccessCheck = function (socialInfo) {
                if (socialInfo.isLithiumSuccessful) {
                    resolve(socialInfo);
                } else {
                    reject();
                }
            };

            // Per Hanah Yendler:
            // 1. Get initial social info from store.
            // 2. If isLithiumSuccessful flag has not been updated to have
            // either true or false, subscribe to watch the social info.
            // 3. Once the value has been added, handle the outcome in
            // lithiumSuccessCheck.
            let initialSocialInfo = store.getState().socialInfo;
            if (initialSocialInfo.isLithiumSuccessful !== null) {
                lithiumSuccessCheck(initialSocialInfo);
            } else {
                let unsubscribe = store.subscribe(socialInfoWatch(newSocialInfo => {
                    unsubscribe();
                    lithiumSuccessCheck(newSocialInfo);
                }), { ignoreAutoUnsubscribe: true });
            }

        });
    },

    getUserSkinTones: function () {
        let userSkinTones = [];

        // If there's a shade_code url param, use it for Anonymous user
        let urlShadeCodeParam = UrlUtils.getParamsByName('shade_code');
        if (userUtils.isAnonymous() && urlShadeCodeParam) {
            return urlShadeCodeParam;
        }

        let { beautyInsiderAccount } = store.getState().user;

        let skinTones = beautyInsiderAccount ? beautyInsiderAccount.skinTones : [];

        if (skinTones && skinTones.length > 0) {
            skinTones.forEach(tone => {
                userSkinTones.push(tone.shadeCode);
            });
        }

        return userSkinTones;
    },

    showFlashPDP: function () {
        let isFlash = this.isFlash() || this.isFlashAutoRenewed();
        let isCanada = localeUtils.isCanada();

        return !this.isAnonymous() ? !isFlash && !isCanada : !isCanada;
    },

    isDefaultBIBirthDay: function (biAccount) {
        let birthday =
            biAccount || store.getState().user.beautyInsiderAccount;
        return birthday && parseInt(birthday.birthMonth) === 1 &&
            parseInt(birthday.birthDay) === 1 &&
            parseInt(birthday.birthYear) === 1800;
    },

    isEligibleForRRC: function () {
        let biAccount = store.getState().user.beautyInsiderAccount;
        return !!biAccount && biAccount.isEligibleForRRC;
    },

    //set global variables for Snapchat and Twitter Conversion pixels
    setSignUpData: function ({ isJoinBi, inStoreUser }) {
        digitalData.user[0].profile[0].profileInfo.inStoreUser = inStoreUser;
        digitalData.user[0].profile[0].profileInfo.isJoinBi = isJoinBi;
    },

    flashAsRougeCelebrationGiftMessage: function () {
        const user = store.getState().user;
        return !!user.flashAsRougeCelebrationGiftMessage;
    },

    forceSignIn: function () {
        const {
            isRichProfilePage,
            isVendorLoginPage,
            isPreviewSettings,
            isVendorGenericLogin
        } = locationUtils;
        const SignInSeen = Storage.local.getItem(LOCAL_STORAGE.SIGN_IN_SEEN);
        const pageDisplayCount = Storage.local.getItem(LOCAL_STORAGE.PAGE_DISPLAY_COUNT);

        // If we don't have PAGE_DISPLAY_COUNT localStorage it means it's the 1st page load
        // PAGE_DISPLAY_COUNT=1 means we've already seen 1 page 
        // We can ignore for subsequent page loads 
        if (!pageDisplayCount) {
            Storage.local.setItem(LOCAL_STORAGE.PAGE_DISPLAY_COUNT, 1);
        } else if (pageDisplayCount === 1) {
            Storage.local.setItem(LOCAL_STORAGE.PAGE_DISPLAY_COUNT, 2);
        }

        if (Sephora.isDesktop() && this.isAnonymous() && !SignInSeen && pageDisplayCount === 1
            && !isRichProfilePage() && !isVendorLoginPage()
            && !isPreviewSettings() && !isVendorGenericLogin()) {
            auth.requireAuthentication(true, null, null)
                .catch(() => { });
        }
    }
};

module.exports = userUtils;
